import { SyntheticEvent, useEffect, useState } from 'react';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import PageHeader from '../../../components/PageHeader';
import { waterlineIcon } from '../../../icons';
import { useAppDispatch } from '../../../redux/store';
import WaterlineRooms from './WaterlineRooms';
import { DisplayResultsBy, OpenLogResultModal, Room, WaterlineItem, WaterlineTestResult } from './types';
import constants from '../../../constants';
import './Waterline.css';
import WaterlineInventory from './WaterlineInventory';
import { Compliance } from '../../../types';
import LogInOfficeResultModal from './components/LogInOfficeResultModal';
import { getWaterlineInventory, getWaterlineRooms, getWaterlineTestResults } from '../../../services/waterlineService';
import { addError } from '../../../redux/alerts';
import useCustomerNumber from '../../../hooks/useCustomerNumber';
import { getWaterlineItemCompliance, getWaterlineTestCompliance } from './services';
import ComplianceContainer from './components/ComplianceContainer';
import WaterlineTestResults from './WaterlineTestResults';

const PracticeWaterline = () => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const [tabIndex, setTabIndex] = useState<number>(0);
  const [testResults, setTestResults] = useState<WaterlineTestResult[]>([]);
  const [inventoryItems, setInventoryItems] = useState<WaterlineItem[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [displayResultsBy, setDisplayResultsBy] = useState<DisplayResultsBy>('roomChair');
  const [waterlineCompliance, setWaterlineCompliance] = useState<Compliance>();
  const [openModal, setOpenModal] = useState<OpenLogResultModal>({ open: false, testResult: null })

  const pageTabs = [
    `Test Results(${testResults.length})`,
    `Inventory(${inventoryItems.length})`,
    'Room/Chair'
  ]

  const customerNumber = useCustomerNumber()

  useEffect(() => {
    if (customerNumber) {
      getTestResults();
      getInventoryItems();
      getRooms();
      getCompliance();
    }
  }, [customerNumber]);

  const getTestResults = async () => {
    try {
      const testResults = await getWaterlineTestResults(customerNumber);
      setTestResults(testResults);
    } catch (error) {
      console.error('Error getting waterline test results:', error);
      dispatch(addError('Error getting waterline test results'));
    }
  };

  const getInventoryItems = async () => {
    try {
      const validSubcategories = ['in-office', 'straws', 'tablets', 'mail-test', 'treatment'];
      const inventoryItems = await getWaterlineInventory(customerNumber);
      const items = inventoryItems.filter((item) =>
        validSubcategories.includes(item.subcategory?.toLowerCase() || '')
      );

      setInventoryItems(items);
    } catch (error) {
      console.error('Error getting waterline inventory:', error);
      dispatch(addError('Error getting waterline inventory'));
    }
  };

  const getRooms = async () => {
    try {
      const rooms = await getWaterlineRooms(customerNumber)
      const sorted = rooms.toSorted((a, b) => a.name.localeCompare(b.name))

      setRooms(sorted)
    } catch (error) {
      console.error('Error getting waterline rooms:', error)
      dispatch(addError('Error getting waterline rooms'))
    }
  }

  const reloadAll = () => {
    getTestResults();
    getInventoryItems();
    getRooms();
  }

  const getCompliance = async () => {
    const [testCompliance, itemCompliance] = await Promise.all([
      getWaterlineTestCompliance(customerNumber),
      getWaterlineItemCompliance(customerNumber)
    ])

    const compliance = {
      test: testCompliance.compliance as Compliance,
      item: itemCompliance.compliance as Compliance,
    }

    const waterlineCompliance = compliance.test.compliance_status !== 4 && (
      compliance.test.compliance_status >= compliance.item.compliance_status || compliance.item.compliance_status === 4
    ) ? compliance.test : compliance.item

    setWaterlineCompliance(waterlineCompliance)
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const TabContent = () => {
    const tabContents = [
      <WaterlineTestResults
        rooms={rooms}
        testResults={testResults}
        inventoryItems={inventoryItems}
        displayResultsBy={displayResultsBy}
        getCompliance={getCompliance}
        getTestResults={getTestResults}
        setTabIndex={setTabIndex}
        setOpenModal={setOpenModal}
        setDisplayResultsBy={setDisplayResultsBy}
      />,
      <WaterlineInventory
        rooms={rooms}
        inventoryItems={inventoryItems}
        onRefresh={getInventoryItems}
      />,
      <WaterlineRooms
        rooms={rooms}
        onRefresh={getRooms}
        onRefreshAll={reloadAll}
      />
    ]

    return tabContents[tabIndex]
  }

  return (
    <>
      <PageHeader src={waterlineIcon} headerName={'Waterline Management'} size={'26px'} />
      {
        testResults.length === 0 && inventoryItems.length === 0
          ? <iframe className="iframe-sales-page" data-testid="sales-iframe" src={constants.WATERLINE_SALES_PAGE_URL} />
          : (
              <>
                <ComplianceContainer waterlineCompliance={waterlineCompliance} />
                <Box sx={{ color: theme.palette.color50555b.main }} className="waterline-container">
                  <Box>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-end m-b-16">
                      <Tabs value={tabIndex} onChange={handleTabChange}>
                        {pageTabs.length && pageTabs.map((tab, index) => <Tab key={index} label={tab} />)}
                      </Tabs>
                    </Box>
                    <TabContent />
                  </Box>
                </Box>
              </>
            )
      }
      {
        openModal.open && (
          <LogInOfficeResultModal
            open={openModal.open}
            setOpenModal={setOpenModal}
            rooms={rooms}
            displayBy={displayResultsBy}
            testResult={openModal.testResult}
            getCompliance={getCompliance}
            getTestResults={getTestResults}
          />
        )
      }
    </>
  )
}

export default PracticeWaterline
